import React from "react";
import { Box, Grid, IconButton, Typography, useTheme } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

import apiService from "../service/Api";
import { tokens } from "../theme";

interface AssetGridProps {
  assets: { id: number; url: string; type: "image" | "video" }[];
  selectMode: boolean;
  setSelectMode: React.Dispatch<React.SetStateAction<boolean>>;
  selectedImages: number[];
  setSelectedImages: React.Dispatch<React.SetStateAction<number[]>>;
  onDelete: (id: number) => void;
}

const AssetGrid: React.FC<AssetGridProps> = ({
  assets,
  selectMode,
  setSelectMode,
  selectedImages,
  setSelectedImages,
  onDelete,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isVideo = (url: string): boolean => {
    return /\.(mp4|webm|ogg|mov)$/i.test(url);
  };
  const [preview, setPreview] = React.useState<{
    url: string;
    type: "image" | "video";
  } | null>(null);

  const downloadAsset = async (url: string, fileName: string) => {
    try {
      // Fetch the image as a blob
      const response = await fetch(url, { mode: "cors" });
      const blob = await response.blob();

      // Create a temporary URL for the blob
      const blobUrl = window.URL.createObjectURL(blob);

      // Create link and trigger download
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName || "image.png";
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Failed to download image", err);
    }
  };

  return (
    <Grid container spacing={2}>
      {assets.map(({ id, url }, index) => {
        const fileName = url.split("/").pop();

        return (
          <Grid size={{ xs: 4 }} key={id}>
            <Box
              sx={{
                position: "relative",
                borderRadius: 2,
                overflow: "hidden",
                cursor: "pointer",
                "&:hover .hover-overlay": { opacity: 1 },
              }}
            >
              {isVideo(url) ? (
                <Box
                  component="video"
                  src={url}
                  muted
                  playsInline
                  preload="metadata"
                  sx={{
                    width: "100%",
                    height: 100,
                    objectFit: "cover",
                    display: "block",
                    cursor: "pointer",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setPreview({ url, type: "video" });
                  }}
                />
              ) : (
                <Box
                  component="img"
                  src={url}
                  alt={fileName}
                  sx={{
                    width: "100%",
                    height: 100,
                    objectFit: "cover",
                    display: "block",
                    cursor: "pointer",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setPreview({ url, type: "image" });
                  }}
                />
              )}

              {isVideo(url) && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    pointerEvents: "none",
                  }}
                >
                  <PlayArrowIcon sx={{ color: "white", fontSize: 32 }} />
                </Box>
              )}

              <Box
                className="hover-overlay"
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  background:
                    "linear-gradient(to top, rgba(0,0,0,0.4), rgba(0,0,0,0.1))",
                  opacity: selectMode ? 1 : 0,
                  transition: "opacity 0.3s",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  pointerEvents: "none",

                  "&:hover": { opacity: 1 },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    p: 0.5,
                    gap: 0.25,
                    pointerEvents: "auto",
                  }}
                >
                  {selectMode ? (
                    <input
                      type="checkbox"
                      checked={selectedImages.includes(index)}
                      onChange={(e) => {
                        e.stopPropagation();
                        let newSelected: number[];
                        if (selectedImages.includes(index)) {
                          newSelected = selectedImages.filter(
                            (i) => i !== index,
                          );
                        } else {
                          newSelected = [...selectedImages, index];
                        }
                        setSelectedImages(newSelected);
                        if (newSelected.length === 0) setSelectMode(false);
                      }}
                      style={{ width: 13, height: 13 }}
                    />
                  ) : (
                    <>
                      <IconButton
                        sx={{
                          fontSize: 8,
                          color: "white",
                          borderRadius: 0,
                          width: 18,
                          height: 18,
                          backgroundColor: "rgba(0,0,0,0.4)",
                          "&:hover": { backgroundColor: "rgba(0,0,0,0.6)" },
                          cursor: "pointer",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectMode(true);
                        }}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>

                      <IconButton
                        sx={{
                          fontSize: 10,
                          color: "white",
                          borderRadius: 0,
                          width: 18,
                          height: 18,
                          backgroundColor: "rgba(0,0,0,0.4)",
                          "&:hover": { backgroundColor: "rgba(0,0,0,0.6)" },
                          cursor: "pointer",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          downloadAsset(url, fileName || `asset-${index}`);
                        }}
                      >
                        <FileDownloadOutlinedIcon fontSize="small" />
                      </IconButton>

                      <IconButton
                        sx={{
                          fontSize: 10,
                          color: "white",
                          borderRadius: 0,
                          width: 18,
                          height: 18,
                          backgroundColor: "rgba(0,0,0,0.4)",
                          "&:hover": { backgroundColor: "rgba(255,0,0,0.7)" },
                        }}
                        onClick={async (e) => {
                          e.stopPropagation();

                          try {
                            await apiService.delete(
                              `/api/configurations/tools-assets/${id}/`,
                            );
                            onDelete(id);
                          } catch (err) {
                            console.error("Failed to delete asset", err);
                          }
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </>
                  )}
                </Box>

                {!selectMode && (
                  <Box
                    sx={{
                      backgroundColor: colors.primary[900],
                      textAlign: "center",
                      py: 0.25,
                      height: 35,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: colors.primary[700],
                        fontSize: "0.6rem",
                        wordBreak: "break-word",
                      }}
                    >
                      {fileName}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Grid>
        );
      })}
      {preview && (
        <Box
          onClick={() => setPreview(null)}
          sx={{
            position: "fixed",
            inset: 0,

            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1300,
          }}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              width: "50%",
              maxWidth: 400,
              maxHeight: "70vh",
              backgroundColor: "rgba(0,0,0,0.6)",
              borderRadius: 2,
              position: "relative",
              p: 1,
            }}
          >
            {/* Close button */}
            <IconButton
              onClick={() => setPreview(null)}
              sx={{
                position: "absolute",
                top: 6,
                right: 6,
                color: "white",
                backgroundColor: "rgba(0,0,0,0.85)",
                "&:hover": { backgroundColor: "rgba(0,0,0,0.7)" },
                zIndex: 1,
              }}
            >
              ✕
            </IconButton>

            {preview.type === "video" ? (
              <Box
                component="video"
                src={preview.url}
                controls
                autoPlay
                sx={{
                  width: "100%",
                  maxHeight: "60vh",
                  objectFit: "contain",
                }}
              />
            ) : (
              <Box
                component="img"
                src={preview.url}
                sx={{
                  width: "100%",
                  maxHeight: "60vh",
                  objectFit: "contain",
                }}
              />
            )}
          </Box>
        </Box>
      )}
    </Grid>
  );
};

export default AssetGrid;
